import { useEffect, useState } from 'react';
import { Users, TrendingUp, Clock, Award, Filter } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import useAuthStore from '../../stores/authStore';

const TeamDashboard = () => {
  const { user } = useAuthStore();
  const [teamStats, setTeamStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('averageScore');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadTeamStats();
  }, []);

  const loadTeamStats = async () => {
    try {
      const response = await api.get('/api/supervisor/team-stats');
      setTeamStats(response.data.managers || []);
    } catch (error) {
      console.error('Error loading team stats:', error);
      toast.error('Ошибка загрузки статистики команды');
    } finally {
      setLoading(false);
    }
  };

  const sortedStats = [...teamStats].sort((a, b) => {
    const aValue = a.stats[sortBy] || 0;
    const bValue = b.stats[sortBy] || 0;
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const calculateTeamAverage = (field) => {
    if (teamStats.length === 0) return 0;
    const sum = teamStats.reduce((acc, user) => acc + (user.stats[field] || 0), 0);
    return Math.round(sum / teamStats.length);
  };

  const calculateTeamTotal = (field) => {
    return teamStats.reduce((acc, user) => acc + (user.stats[field] || 0), 0);
  };

  if (user?.role !== 'supervisor' && user?.role !== 'director' && user?.role !== 'admin') {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">У вас нет доступа к этой странице</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка статистики команды...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Управление командой</h1>
        <p className="text-gray-600 mt-1">Результаты и активность менеджеров вашей команды</p>
      </div>

      {/* Team Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Всего менеджеров</h3>
          <p className="text-3xl font-bold text-gray-900">{teamStats.length}</p>
          <p className="text-sm text-gray-500 mt-2">В вашей команде</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Award className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Средний балл команды</h3>
          <p className="text-3xl font-bold text-gray-900">
            {calculateTeamAverage('averageScore')}%
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {calculateTeamAverage('averageScore') >= 70 ? 'Отличный результат!' : 'Есть куда расти'}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Общее время команды</h3>
          <p className="text-3xl font-bold text-gray-900">
            {Math.round(calculateTeamTotal('totalDuration') / 60000)} мин
          </p>
          <p className="text-sm text-gray-500 mt-2">Все тренировки</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Всего сессий</h3>
          <p className="text-3xl font-bold text-gray-900">
            {calculateTeamTotal('totalSessions')}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Завершено: {calculateTeamTotal('completedSessions')}
          </p>
        </div>
      </div>

      {/* Team Members Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Менеджеры</h2>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Filter className="w-4 h-4" />
              <span>Сортировка: </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="averageScore">По баллу</option>
                <option value="totalSessions">По сессиям</option>
                <option value="completedSessions">По завершенным</option>
                <option value="totalDuration">По времени</option>
              </select>
            </div>
          </div>
        </div>

        {teamStats.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium mb-2">В вашей команде пока нет менеджеров</p>
            <p className="text-sm">Обратитесь к директору для добавления менеджеров в команду</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Менеджер
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('totalSessions')}
                  >
                    Сессий {sortBy === 'totalSessions' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('averageScore')}
                  >
                    Средний балл {sortBy === 'averageScore' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Производительность
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedStats.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {member.firstName && member.lastName
                            ? `${member.firstName} ${member.lastName}`
                            : member.email}
                        </p>
                        {member.firstName && <p className="text-sm text-gray-500">{member.email}</p>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900">{member.stats.totalSessions}</span>
                        <span className="text-xs text-gray-500">
                          ({member.stats.completedSessions} завершено)
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {Math.round(member.stats.averageScore)}%
                        </span>
                        {member.stats.averageScore >= 70 ? (
                          <TrendingUp className="w-4 h-4 text-green-500" />
                        ) : member.stats.averageScore >= 50 ? (
                          <span className="text-yellow-500">—</span>
                        ) : (
                          <TrendingUp className="w-4 h-4 text-red-500 transform rotate-180" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {member.stats.averageScore >= 70 && member.stats.completedSessions >= 5 ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Отлично
                        </span>
                      ) : member.stats.averageScore >= 50 && member.stats.completedSessions >= 3 ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          Хорошо
                        </span>
                      ) : member.stats.totalSessions > 0 ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                          Требует внимания
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          Нет данных
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">💡 Рекомендации для команды</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          {teamStats.filter(u => !u.stats.totalSessions || u.stats.totalSessions === 0).length > 0 && (
            <li>
              • {teamStats.filter(u => !u.stats.totalSessions || u.stats.totalSessions === 0).length} менеджер(ов) ещё не начали тренировки. Напомните им завершить обучение и перейти к практике.
            </li>
          )}
          {calculateTeamAverage('averageScore') < 60 && (
            <li>
              • Средний балл команды ниже 60%. Рекомендуется провести дополнительное обучение или увеличить частоту тренировок.
            </li>
          )}
          {teamStats.filter((u) => u.stats.totalSessions < 3 && u.stats.totalSessions > 0).length > 0 && (
            <li>
              • {teamStats.filter((u) => u.stats.totalSessions < 3 && u.stats.totalSessions > 0).length} менеджер(ов) имеют менее 3 тренировок. Мотивируйте их к регулярной практике.
            </li>
          )}
          {teamStats.filter((u) => u.stats.averageScore < 50 && u.stats.totalSessions > 0).length > 0 && (
            <li>
              • {teamStats.filter((u) => u.stats.averageScore < 50 && u.stats.totalSessions > 0).length} менеджер(ов) показывают низкие результаты. Уделите им особое внимание и дополнительную поддержку.
            </li>
          )}
          {calculateTeamAverage('averageScore') >= 70 && teamStats.filter(u => u.stats.totalSessions > 0).length > 0 && (
            <li>
              • ✨ Отличные результаты команды! Продолжайте мотивировать сотрудников и рассмотрите введение более сложных сценариев.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default TeamDashboard;
